import { Injectable } from '@angular/core';
import { CountryModel } from 'lemunzo-api';
import {
    CountryCode,
    getCountries,
    getCountryCallingCode,
    isSupportedCountry,
} from 'libphonenumber-js/min';

/**
 * Single country entry renderable by the phone-input dropdown.
 * `iso2` is lowercase to stay consistent with `CountryModel.code` and flag-code usage.
 */
export type PhoneCountryOption = {
    iso2: string;
    dialCode: string;
    name: string;
};

type BuildPhoneCountryListArgs = {
    /**
     * Localized `{ code, isSupported, name }` for the current language
     * (typically pulled from `LocalizationService.localizedCountries$`).
     */
    localizedNames: CountryModel[];

    /**
     * BCP 47 language tag used for locale-aware alphabetical sorting.
     */
    language: string;

    /**
     * Optional whitelist of ISO2 codes (case-insensitive). When `undefined`,
     * all supported countries are included. An empty array means no country
     * is allowed (the resulting list will be empty).
     */
    allowedCountries?: string[];

    /**
     * Optional list of ISO2 codes (case-insensitive) that should be pinned
     * to the top of the list. Duplicates are ignored (first occurrence wins);
     * entries not in `allowedCountries` or not supported by the library are
     * dropped. Remaining countries are sorted alphabetically by localized name.
     */
    priorityCountries?: string[];
};

/**
 * Single source of truth for country dial-code metadata used by the enhanced phone input.
 * Country metadata (ISO2 + dial code) comes from `libphonenumber-js`, while localized names are provided
 * by the caller to keep this service independent from any i18n implementation.
 *
 * @note This service is not exported from the `public-api.ts` as it is used only in `lemunzo-shared` currently
 *
 * @link https://github.com/catamphetamine/libphonenumber-js
 */
@Injectable({
    providedIn: 'root',
})
export class PhoneCountryService {
    private readonly dialCodes: ReadonlyMap<string, string> =
        this.buildDialCodeMap();

    /**
     * Returns a dial code (without `+`) for a country ISO2 code.
     *
     * @param iso2 Two-letter ISO2 country code (case-insensitive).
     * @returns Dial code without `+`, or `undefined` when the country is unsupported.
     */
    public getDialCode(iso2: CountryCode): string | undefined {
        return this.dialCodes.get(this.normalizeIso2(iso2));
    }

    /**
     * Builds a localized render list for the country picker.
     * Emits only countries present in `localizedNames` so every option has a curated localized
     * name and matching flag code. Priority countries are pinned first (input order preserved);
     * remaining countries are locale-sorted alphabetically.
     *
     * @param args Localized names, active language, and optional allow/priority country filters.
     * @returns Ordered list of country options for dropdown rendering.
     *
     * @link https://developer.mozilla.org/docs/Web/JavaScript/Reference/Global_Objects/Intl/Collator
     */
    public buildDisplayList(
        args: BuildPhoneCountryListArgs
    ): PhoneCountryOption[] {
        const allowed = this.normalizeAllowList(args.allowedCountries);
        const nameByIso2 = new Map(
            args.localizedNames.map((entry) => [
                this.normalizeIso2(entry.code),
                entry.name,
            ])
        );

        const { priority, prioritySet } = this.resolvePriorityCountries(
            args.priorityCountries,
            nameByIso2,
            allowed
        );

        const options = this.buildOptions(nameByIso2, allowed);

        return this.orderOptions(options, priority, prioritySet, args.language);
    }

    private buildDialCodeMap(): Map<string, string> {
        // Build lowercase ISO2 -> dial-code map once at service startup.
        const map = new Map<string, string>();

        for (const code of getCountries()) {
            if (!isSupportedCountry(code)) {
                continue;
            }

            map.set(
                this.normalizeIso2(code),
                getCountryCallingCode(code as CountryCode)
            );
        }

        return map;
    }

    private normalizeAllowList(
        allowedCodes: string[] | undefined
    ): Set<string> | null {
        // `undefined` means "no restriction"; otherwise use lowercase O(1) lookup.
        if (allowedCodes === undefined) {
            return null;
        }

        return new Set(allowedCodes.map((code) => this.normalizeIso2(code)));
    }

    private resolvePriorityCountries(
        priorityCountries: string[] | undefined,
        nameByIso2: ReadonlyMap<string, string>,
        allowed: ReadonlySet<string> | null
    ): { priority: string[]; prioritySet: Set<string> } {
        // Array preserves caller order for pinned items; set is for O(1) lookup.
        const prioritySet = new Set<string>();
        const priority: string[] = [];

        for (const raw of priorityCountries ?? []) {
            const code = this.normalizeIso2(raw);
            if (prioritySet.has(code)) {
                continue;
            }

            if (!this.dialCodes.has(code) || !nameByIso2.has(code)) {
                continue;
            }

            if (allowed && !allowed.has(code)) {
                continue;
            }

            prioritySet.add(code);
            priority.push(code);
        }

        return { priority, prioritySet };
    }

    private buildOptions(
        nameByIso2: ReadonlyMap<string, string>,
        allowed: ReadonlySet<string> | null
    ): PhoneCountryOption[] {
        // Include only allowed countries that also have curated localized names.
        const options: PhoneCountryOption[] = [];

        for (const [iso2, dialCode] of this.dialCodes) {
            if (allowed && !allowed.has(iso2)) {
                continue;
            }

            const name = nameByIso2.get(iso2);

            if (!name) {
                continue;
            }

            options.push({ iso2, dialCode, name });
        }

        return options;
    }

    private orderOptions(
        options: ReadonlyArray<PhoneCountryOption>,
        priority: ReadonlyArray<string>,
        prioritySet: ReadonlySet<string>,
        language: string
    ): PhoneCountryOption[] {
        // Keep pinned countries first, then locale-aware alphabetical order for the rest.
        const collator = new Intl.Collator(language, {
            sensitivity: 'base',
        });

        const priorityOptions = priority
            .map((code) => options.find((option) => option.iso2 === code))
            .filter((option): option is PhoneCountryOption => !!option);

        const restOptions = options
            .filter((option) => !prioritySet.has(option.iso2))
            .sort((a, b) => collator.compare(a.name, b.name));

        return [...priorityOptions, ...restOptions];
    }

    private normalizeIso2(code: string): string {
        return code.toLowerCase();
    }
}
